import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  IconButton, 
  Button, 
  Box, 
  Typography, 
  Divider,
  Chip
} from '@mui/material';
import { 
  XMarkIcon, 
  ArrowDownTrayIcon, 
  CheckBadgeIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

const CertificatePreviewModal = ({ open, onClose, certificate }) => {
  if (!certificate) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    if (certificate.certificate_pdf_url) {
      window.open(certificate.certificate_pdf_url, '_blank');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          overflow: 'hidden',
          bgcolor: '#F8FAFC'
        }
      }}
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #E2E8F0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              bgcolor: 'rgba(132, 63, 239, 0.1)', 
              borderRadius: '12px' 
            }}>
              <CheckBadgeIcon className="h-6 w-6 text-[#843FEF]" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                Certificate Preview
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Verified Achievement
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={onClose} sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
              <XMarkIcon className="h-6 w-6" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 350px', xs: '1fr' }, gap: 4 }}>
            
            {/* Certificate Display Area */}
            <Box sx={{ 
              bgcolor: '#FFFFFF', 
              borderRadius: '16px', 
              p: 2, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: '400px',
              justifyContent: 'center',
              border: '1px solid #E2E8F0',
              overflow: 'hidden'
            }}>
              {certificate.certificate_image_url ? (
                <img 
                  src={certificate.certificate_image_url} 
                  alt="Certificate" 
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="h4" sx={{ color: '#843FEF', fontWeight: 900, mb: 2 }}>
                    CERTIFICATE OF ACHIEVEMENT
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#64748B', mb: 1 }}>
                    This is to certify that
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mb: 3 }}>
                    {certificate.studentName || 'Student Name'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#64748B', mb: 1 }}>
                    has successfully completed the program
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#843FEF', mb: 3 }}>
                    {certificate.programName || certificate.courseName || 'Program Name'}
                  </Typography>
                  <Divider sx={{ width: '60%', mb: 3 }} />
                  <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                    ID: {certificate.certificateId || certificate._id}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Details & Actions Panel */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ bgcolor: '#FFFFFF', p: 3, borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Certificate Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <DetailRow label="Program" value={certificate.programName || certificate.courseName} />
                  <DetailRow label="College" value={certificate.collegeName} />
                  <DetailRow label="Issue Date" value={certificate.issueDate ? new Date(certificate.issueDate).toLocaleDateString() : 'N/A'} />
                  <DetailRow label="Grade" value={certificate.grade || 'A+'} />
                  <DetailRow label="Certificate ID" value={certificate.certificateId || certificate._id} isCode />
                </Box>

                <Box sx={{ mt: 3 }}>
                   <Chip 
                    icon={<CheckBadgeIcon className="h-4 w-4" style={{ color: '#059669' }} />}
                    label="Verified Credentials" 
                    sx={{ 
                      bgcolor: '#ECFDF5', 
                      color: '#059669', 
                      fontWeight: 700,
                      borderRadius: '8px',
                      border: '1px solid #D1FAE5'
                    }} 
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  startIcon={<ArrowDownTrayIcon className="h-5 w-5" />}
                  onClick={handleDownload}
                  sx={{ 
                    bgcolor: '#843FEF', 
                    borderRadius: '12px',
                    py: 1.5,
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(132, 63, 239, 0.2)',
                    '&:hover': { bgcolor: '#7035CC', boxShadow: '0 6px 16px rgba(132, 63, 239, 0.3)' }
                  }}
                >
                  Download Certificate
                </Button>
                
                <Button 
                  fullWidth 
                  variant="outlined" 
                  startIcon={<PrinterIcon className="h-5 w-5" />}
                  onClick={handlePrint}
                  sx={{ 
                    borderRadius: '12px',
                    py: 1.5,
                    fontWeight: 700,
                    borderColor: '#E2E8F0',
                    color: '#64748B',
                    '&:hover': { bgcolor: '#F1F5F9', borderColor: '#CBD5E1' }
                  }}
                >
                  Print Friendly View
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

const DetailRow = ({ label, value, isCode }) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ 
      color: '#334155', 
      fontWeight: 700,
      fontFamily: isCode ? 'monospace' : 'inherit',
      wordBreak: 'break-all'
    }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

export default CertificatePreviewModal;
